import { plainSet } from '@ipfabric/utils/fp/plainSet';
import type { ReactNode } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import type { TreePickerItemWithValue } from './helpers/TreePickerItem';
import { getTreePickerItemKeys } from './helpers/getTreePickerItemKeys';

interface Props<TKey> {
  item: TreePickerItemWithValue<TKey>;
  value: Set<TKey>;
  onChange: (items: Set<TKey>) => void;
}

export const TreePickerCheckbox = <TKey extends string>(
  props: Props<TKey>,
): ReactNode => {
  const disabled = areAllChildrenDisabled(props.item);
  const handleChange = (value: boolean) => {
    const keys = getTreePickerItemKeys(props.item, true);

    const selectAll = () => props.onChange(plainSet.union(props.value)(keys));
    const deselectAll = () => props.onChange(plainSet.diff(keys)(props.value));
    if (value) {
      if (atLeastOneIsSelected(keys, props.value)) {
        deselectAll();
      } else {
        selectAll();
      }
    } else {
      deselectAll();
    }
  };

  return (
    <Checkbox
      value={props.item.value}
      onChange={handleChange}
      disabled={props.item.disabled || disabled}
    />
  );
};

const atLeastOneIsSelected = (keys: Set<string>, selected: Set<string>) => {
  for (const item of keys) {
    if (selected.has(item)) {
      return true;
    }
  }
  return false;
};

const areAllChildrenDisabled = (
  item: TreePickerItemWithValue<string>,
): boolean => {
  if (item.children.length === 0) {
    return item.disabled ?? false;
  }
  return item.children.every((child) => areAllChildrenDisabled(child));
};
